import { create } from 'zustand';
import type { AuthUser } from '@eriko/shared-types';

interface AdminAuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;

  setAuth: (data: {
    user: AuthUser;
    tokens: { accessToken: string; refreshToken: string };
  }) => void;
  updateTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  hydrateFromStorage: () => boolean;
}

const TOKEN_KEY = 'eriko_admin_access_token';
const REFRESH_KEY = 'eriko_admin_refresh_token';
const USER_KEY = 'eriko_admin_user';

function persistToStorage(state: Partial<AdminAuthState>) {
  if (state.accessToken) localStorage.setItem(TOKEN_KEY, state.accessToken);
  if (state.refreshToken) localStorage.setItem(REFRESH_KEY, state.refreshToken);
  if (state.user) localStorage.setItem(USER_KEY, JSON.stringify(state.user));
}

function clearStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export const useAuthStore = create<AdminAuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoading: true,

  setAuth: ({ user, tokens }) => {
    const state = {
      user,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      isLoading: false,
    };
    persistToStorage(state);
    set(state);
  },

  updateTokens: (tokens) => {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },

  clearAuth: () => {
    clearStorage();
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  hydrateFromStorage: () => {
    const accessToken = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const userRaw = localStorage.getItem(USER_KEY);

    if (!accessToken || !refreshToken || !userRaw) {
      set({ isLoading: false });
      return false;
    }

    try {
      const user = JSON.parse(userRaw) as AuthUser;
      set({ user, accessToken, refreshToken, isLoading: false });
      return true;
    } catch {
      clearStorage();
      set({ isLoading: false });
      return false;
    }
  },
}));
